import { Page, View, Text, Link } from '@react-pdf/renderer';
import { styles } from './styles';
import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import { globalStyles } from '../../styles';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { getImageSrc } from '@utils/fn';
import { APP_HOST } from '@utils/apiHooks';
import { SafeImage } from '@composites/PDF/AgendaPdfContent/components/SafeImage';

export interface CoLocatedEventsPageProps {
  event: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  footer: JSX.Element;
}

const CoLocatedEventsPage = ({
  event,
  coLocatedEvents,
  footer,
}: CoLocatedEventsPageProps) => {
  const { t } = useTranslation();

  const coEvents = useMemo(() => {
    return coLocatedEvents?.filter(e => e?.id !== event?.id) || [];
  }, [coLocatedEvents, event?.id]);

  // Helper: Remove year from event name
  const getNameWithoutYear = (name?: string) =>
    name ? name.replace(/\s*\d{4}\s*$/, '').trim() : '';

  // Helper: Get comma-separated event names (without year)
  const coEventNames = coEvents
    .map(e => getNameWithoutYear(e?.name))
    .filter(Boolean);

  const eventNameNoYear = getNameWithoutYear(event?.name);
  const coEventCount = coEvents.length;

  const getItem = ({
    logo,
    text,
    link,
    coEventCount,
  }: {
    logo: string;
    text: string;
    link: string;
    coEventCount: number;
  }) => {
    return (
      <View
        style={{
          ...styles.itemWrapper,
          paddingVertical: coEventCount > 4 ? 30 : 40,
          paddingHorizontal: coEventCount > 4 ? 30 : 40,
        }}
      >
        <View
          style={{
            ...styles.itemContentWrapper,
          }}
        >
          <SafeImage src={logo} style={styles.logoImage} />
          <Text
            style={{
              ...styles.itemTitle,
              fontSize: coEventCount > 4 ? 14 : 16,
              marginTop: coEventCount > 4 ? 10 : 20,
            }}
          >
            {text}
          </Text>
        </View>

        <View
          style={{
            width: '100%',
          }}
        >
          <Link
            src={link}
            style={{
              ...styles.button,
              backgroundColor: event?.design?.themeColor ?? '#000',
              color: 'white',
              width: '100%',
            }}
          >
            {t('view-event')}
          </Link>
        </View>
      </View>
    );
  };

  return (
    <Page
      size={{ width: 1440, height: 800 }}
      style={{ ...styles.page, overflow: 'hidden' }}
    >
      <View style={{ ...styles.leftColumn }}>
        <View style={{ marginBottom: 40 }}>
          <Text style={{ ...styles.title }}>{'CO-LOCATED EVENTS'}</Text>
          <View
            style={{
              ...globalStyles.lineBar,
              borderColor: event?.design?.themeColor ?? 'black',
              marginLeft: 0,
            }}
          />
        </View>

        <Text style={{ ...styles.text, marginTop: 10 }}>
          {t('colocated-events-description', { eventName: eventNameNoYear })}
        </Text>

        <Text style={{ ...styles.text, marginTop: 10, marginBottom: 10 }}>
          {t('colocated-events-list-title')}
        </Text>

        <View style={{}}>
          <Text style={{ ...styles.text, lineHeight: 1.8 }}>
            <Text>{`• ${t('colocated-events-list-item-1')}`}</Text>
            <Text
              style={{ fontWeight: 'bold' }}
            >{`${t('colocated-events-list-item-1-bold', { nrOfColocatedEvents: coEventCount })}`}</Text>
          </Text>
          <Text style={{ ...styles.text, lineHeight: 1.8 }}>
            <Text>{`• ${t('colocated-events-list-item-2', { nrOfColocatedEvents: coEventCount })}`}</Text>
            <Text
              style={{ fontWeight: 'bold' }}
            >{`${t('colocated-events-list-item-2-bold', { nrOfColocatedEvents: coEventCount })}`}</Text>
          </Text>
          <Text style={{ ...styles.text, lineHeight: 1.8 }}>
            {`• ${t('colocated-events-list-item-3')}`}
          </Text>
        </View>

        <Text style={{ ...styles.text, marginTop: 5 }}>
          <Text>
            {t('colocated-events-paragraph-1-start', {
              eventNames: coEventNames.join(', '),
            })}
          </Text>
          <Text style={{ fontWeight: 'bold' }}>
            {t('colocated-events-paragraph-1-bold')}
          </Text>
          <Text>{t('colocated-events-paragraph-1-end')}</Text>
        </Text>

        <View style={{ ...styles.text, marginTop: 10 }}>
          <Text style={styles.paragraphBold}>
            {t('colocated-events-paragraph-2')}
          </Text>{' '}
          <Text>{t('colocated-events-paragraph-3')}</Text>
        </View>
      </View>

      {/* RIGHT COLUMN: Event Tiles in Rows */}
      <View style={styles.rightColumn}>
        {/* Render event tiles in rows as specified */}
        {(() => {
          // Layout rules: [row1, row2] tile counts by coEventCount
          const layoutMap: Record<number, number[]> = {
            2: [2, 0],
            3: [2, 1],
            4: [2, 2],
            5: [3, 2],
            6: [3, 3],
          };
          const layout = layoutMap[coEventCount] || [coEventCount, 0];
          let idx = 0;
          return layout.map((tiles, rowIdx) => {
            if (!tiles) return null;
            const rowEvents = coEvents.slice(idx, idx + tiles);
            idx += tiles;
            return (
              <View
                key={rowIdx}
                style={{
                  flexDirection: 'row',
                  width: '100%',
                  gap: 20,
                  marginBottom: 20,
                }}
              >
                {rowEvents.map((e, i) => (
                  <View key={e?.id || i} style={{ flex: 1 }}>
                    {getItem({
                      logo: getImageSrc(e?.images?.logo ?? ''),
                      text: e?.description ?? '',
                      link: `${APP_HOST}/events/${e?.slug}`,
                      coEventCount: coEventCount,
                    })}
                  </View>
                ))}
              </View>
            );
          });
        })()}
      </View>

      {footer}
    </Page>
  );
};

export default CoLocatedEventsPage;
